import React, {
    createContext,
    HTMLAttributes,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import cn from 'classnames';

import cls from './UiDropdown.module.css';

interface UiDropdownRenderTriggerProps {
    ref: (node: HTMLElement | null) => void;
    isOpen: boolean;
    referenceProps: HTMLAttributes<HTMLElement>;
}

interface UiDropdownProps {
    renderTrigger: (props: UiDropdownRenderTriggerProps) => ReactNode;
    children: ReactNode | ((props: { close: () => void }) => ReactNode);
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    placement?: Placement;
    offsetValue?: number;
    className?: string;
    disabled?: boolean;
    closeOnItemClick?: boolean;
}

interface UiDropdownContextValue {
    close: () => void;
    closeOnItemClick: boolean;
}

const UiDropdownContext = createContext<UiDropdownContextValue | null>(null);

const List = ({ children, className }: PropsWithChildren<{ className?: string }>) => (
    <ul className={cn(cls.list, className)} role="menu">
        {children}
    </ul>
);

interface ListItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
    closeOnClick?: boolean;
}

const ListItem = ({
    children,
    className,
    closeOnClick,
    onClick,
    onKeyDown,
    ...rest
}: PropsWithChildren<ListItemProps>) => {
    const dropdownContext = useContext(UiDropdownContext);
    const shouldClose = closeOnClick ?? dropdownContext?.closeOnItemClick ?? true;

    const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
        onClick?.(event);

        if (!event.defaultPrevented && shouldClose) {
            dropdownContext?.close();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        onKeyDown?.(event);

        if (event.defaultPrevented) {
            return;
        }

        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.currentTarget.click();
    };

    return (
        <li
            className={cn(cls.listItem, className)}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="menuitem"
            tabIndex={0}
            {...rest}
        >
            {children}
        </li>
    );
};

type UiDropdownCompoundComponent = React.FC<UiDropdownProps> & {
    List: typeof List;
    ListItem: typeof ListItem;
};

const UiDropdownComponent: UiDropdownCompoundComponent = ({
    renderTrigger,
    children,
    isOpen: controlledIsOpen,
    onOpenChange,
    placement = 'bottom-start',
    offsetValue = 6,
    className,
    disabled = false,
    closeOnItemClick = true,
}) => {
    const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
    const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            setUncontrolledIsOpen(nextIsOpen);
            onOpenChange?.(nextIsOpen);
        },
        [onOpenChange]
    );

    const close = useCallback(() => handleOpenChange(false), [handleOpenChange]);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: handleOpenChange,
        placement,
        middleware: [offset(offsetValue), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context, { enabled: !disabled });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const dropdownContextValue = useMemo(
        () => ({
            close,
            closeOnItemClick,
        }),
        [close, closeOnItemClick]
    );

    return (
        <>
            {renderTrigger({
                ref: refs.setReference,
                isOpen,
                referenceProps: getReferenceProps({
                    'aria-expanded': isOpen,
                    'aria-haspopup': 'menu',
                    disabled,
                }) as HTMLAttributes<HTMLElement>,
            })}

            <FloatingPortal>
                {isOpen && !disabled && (
                    <UiDropdownContext.Provider value={dropdownContextValue}>
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className={cn(cls.dropdown, className)}
                            {...getFloatingProps()}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {typeof children === 'function' ? children({ close }) : children}
                        </div>
                    </UiDropdownContext.Provider>
                )}
            </FloatingPortal>
        </>
    );
};

UiDropdownComponent.List = List;
UiDropdownComponent.ListItem = ListItem;

export { UiDropdownComponent as UiDropdown };
